import React from 'react';
import PropTypes from 'prop-types';

import {Box, Button, Dialog, IconButton, Stack, Typography, TabContext, TabList, Tab} from 'views/components';
import {Close} from 'views/icons';
import {Translate, translate} from 'views/module';

import AutomationsStepBasic from './steps/basic/AutomationsStepBasic';
import AutomationsStepRules from './steps/rules/AutomationsStepRules';

import getAutomationRules from '../util/automationRules';
import {
  buildAutomationDraft,
  buildDefaultVisibleUpdateRules,
  buildSubmissionUpdateRules,
  isCustomAutomation,
  updateFieldValueInUpdateRules,
} from '../util/automationHelper';

const AutomationsNew = (props) => {
  const {groupOptions, onClose, handleCreateAutomation, automation} = props;

  const hasExistingGroups = groupOptions.length > 0;
  const defaultGroupMode = hasExistingGroups ? 'existing' : 'new';
  const defaultExistingGroup = hasExistingGroups ? groupOptions[0] : '';
  const stepKeys = ['BASIC', 'RULES'];
  const automationRules = React.useMemo(() => getAutomationRules(), []);

  const [automationForm, setAutomationForm] = React.useState(() => buildAutomationDraft(automationRules, automation));
  const [currentStep, setCurrentStep] = React.useState(0);
  const [groupMode, setGroupMode] = React.useState(() => {
    if (automation) {
      return groupOptions.includes(automation.groupName) ? 'existing' : 'new';
    }

    return defaultGroupMode;
  });
  const [existingGroup, setExistingGroup] = React.useState(() => {
    return automation?.groupName || defaultExistingGroup;
  });
  const [newGroupName, setNewGroupName] = React.useState(() => {
    if (automation && !groupOptions.includes(automation.groupName)) {
      return automation.groupName;
    }

    return '';
  });

  const currentStepKey = stepKeys[currentStep];
  const groupName = automationForm.groupName.trim();
  const [isTriggerLocked] = React.useState(() => {
    if (!automation) {
      return false;
    }

    return isCustomAutomation(automationRules, automation.resourceType, automation.updateRules);
  });
  const isBasicStepValid = Boolean(
      automationForm.title.trim() &&
      automationForm.description.blob.trim() &&
      groupName,
  );
  const hasValidUpdateRule = isTriggerLocked || automationForm.updateRules.length > 0;
  const isSubmitDisabled = !(
    isBasicStepValid &&
    hasValidUpdateRule &&
    automationForm.prompt.trim() &&
    Array.isArray(automationForm.assigneeRns)
  );

  const setAutomationName = React.useCallback((nextTitle) => {
    setAutomationForm((currentAutomationForm) => ({
      ...currentAutomationForm,
      title: nextTitle,
    }));
  }, []);

  const setDescription = React.useCallback((nextDescription) => {
    setAutomationForm((currentAutomationForm) => ({
      ...currentAutomationForm,
      description: {
        ...currentAutomationForm.description,
        blob: nextDescription,
      },
    }));
  }, []);

  const setAutomationPrompt = React.useCallback((nextPrompt) => {
    setAutomationForm((currentAutomationForm) => ({
      ...currentAutomationForm,
      prompt: nextPrompt,
    }));
  }, []);

  const setAutomationPlaybookRn = React.useCallback((nextPlaybookRn) => {
    setAutomationForm((currentAutomationForm) => ({
      ...currentAutomationForm,
      playbookRn: nextPlaybookRn,
    }));
  }, []);

  const setAutomationAssigneeRns = React.useCallback((nextAssigneeRns) => {
    setAutomationForm((currentAutomationForm) => ({
      ...currentAutomationForm,
      assigneeRns: nextAssigneeRns,
    }));
  }, []);

  const setAutomationResourceType = React.useCallback((nextResourceType) => {
    setAutomationForm((currentAutomationForm) => ({
      ...currentAutomationForm,
      resourceType: nextResourceType,
      updateRules: [],
    }));
  }, []);

  const setAutomationUpdateRuleField = React.useCallback((field, nextValue) => {
    setAutomationForm((currentAutomationForm) => ({
      ...currentAutomationForm,
      updateRules: updateFieldValueInUpdateRules(currentAutomationForm.updateRules, field, nextValue),
    }));
  }, []);

  React.useEffect(() => {
    setAutomationForm((currentAutomationForm) => ({
      ...currentAutomationForm,
      groupName: groupMode === 'existing' ? existingGroup : newGroupName,
    }));
  }, [existingGroup, groupMode, newGroupName]);

  React.useEffect(() => {
    if (currentStepKey !== 'RULES' || isTriggerLocked || automationForm.updateRules.length > 0) {
      return;
    }

    setAutomationForm((currentAutomationForm) => ({
      ...currentAutomationForm,
      updateRules: buildDefaultVisibleUpdateRules(automationRules, currentAutomationForm.resourceType),
    }));
  }, [automationForm.updateRules.length, automationRules, currentStepKey, isTriggerLocked]);

  const closeDialog = React.useCallback(() => {
    onClose();
  }, [onClose]);

  const goToNextStep = React.useCallback(() => {
    if (!isBasicStepValid) {
      return;
    }

    setCurrentStep((value) => Math.min(value + 1, stepKeys.length - 1));
  }, [isBasicStepValid, stepKeys.length]);

  const goToPreviousStep = React.useCallback(() => {
    setCurrentStep((value) => Math.max(value - 1, 0));
  }, []);

  const setCurrentTab = React.useCallback((_event, value) => {
    setCurrentStep(stepKeys.indexOf(value));
  }, [stepKeys]);

  const handleSubmit = React.useCallback(() => {
    if (isSubmitDisabled) {
      return;
    }

    const automationPayload = {
      ...automationForm,
      groupName,
      updateRules: buildSubmissionUpdateRules(
          automationRules,
          automationForm.resourceType,
          automationForm.updateRules,
          isTriggerLocked,
      ),
    };

    handleCreateAutomation(automationPayload, automation?.taskRuleRn);
  }, [automationForm, automationRules, groupName, handleCreateAutomation, isSubmitDisabled, isTriggerLocked, automation?.taskRuleRn]);

  return (
    <Dialog
      onClose={closeDialog}
      maxWidth={false}
      BackdropProps={{
        sx: {
          backgroundColor: 'rgba(26, 25, 22, 0.28)',
        },
      }}
      PaperProps={{
        sx: {
          width: 'calc(100vw - 24px)',
          maxWidth: '692px',
          m: '12px',
          borderRadius: '22px',
          border: '1px solid #EDE8E2',
          boxShadow: '0px 28px 90px rgba(26, 25, 22, 0.16)',
          overflow: 'hidden',
          backgroundColor: '#FFFFFF',
        },
      }}
    >
      <Stack
        sx={{
          width: '100%',
        }}
      >
        <Stack
          sx={{
            px: {
              xs: '20px',
              sm: '26px',
            },
            pt: '16px',
            pb: '14px',
            gap: '10px',
          }}
        >
          <Stack
            direction='row'
            alignItems='flex-start'
            justifyContent='space-between'
            sx={{
              gap: '16px',
            }}
          >
            <Stack sx={{gap: '10px'}}>
              <Typography
                sx={{
                  fontSize: '11px',
                  fontWeight: 400,
                  color: '#A8A49E',
                  lineHeight: '100%',
                }}
              >
                <Translate expression='automations.Automations.New.Eyebrow' />
              </Typography>
              <Typography
                sx={{
                  fontSize: '17px',
                  fontWeight: 500,
                  color: '#1A1916',
                  lineHeight: '120%',
                }}
              >
                <Translate expression='automations.Automations.New.Title' />
              </Typography>
            </Stack>
            <IconButton
              onClick={closeDialog}
              sx={{
                'width': '28px',
                'height': '28px',
                'borderRadius': '7px',
                'border': '1px solid #E8E4DE',
                'color': '#B0AAA2',
                'backgroundColor': '#FFFFFF',
                'flexShrink': 0,
                '&:hover': {
                  backgroundColor: '#FFFFFF',
                  borderColor: '#E8E4DE',
                },
              }}
            >
              <Close
                sx={{
                  fontSize: '16px',
                }}
              />
            </IconButton>
          </Stack>
        </Stack>

        <TabContext value={currentStepKey}>
          <TabList
            onChange={setCurrentTab}
            textColor='inherit'
            sx={{
              'minHeight': 'auto',
              'mb': 0,
              'width': 'calc(100% + 52px)',
              'mx': {
                xs: '-20px',
                sm: '-26px',
              },
              'px': {
                xs: '20px',
                sm: '26px',
              },
              '& .MuiTabs-flexContainer': {
                p: 0,
                m: 0,
                gap: 0,
              },
              '& .MuiTab-root': {
                minHeight: 'auto',
                minWidth: '4rem',
                p: 1,
                m: 0,
                color: '#A8A49E',
                textTransform: 'none',
              },
              '& .MuiTab-root.Mui-selected': {
                color: '#DA7955',
              },
              '& .MuiTabs-indicator': {
                backgroundColor: '#DA7955',
              },
            }}
          >
            <Tab
              label={<Translate expression='automations.Automations.New.Steps.Basic.Title' />}
              value='BASIC'
            />
            <Tab
              label={<Translate expression='automations.Automations.New.Steps.Rules.Title' />}
              value='RULES'
              disabled={!isBasicStepValid}
            />
          </TabList>
        </TabContext>

        <Box sx={{borderBottom: '1px solid #EEE8E2'}}></Box>

        {
          currentStepKey === 'BASIC' &&
          <AutomationsStepBasic
            automationName={automationForm.title}
            existingGroup={existingGroup}
            groupMode={groupMode}
            groupOptions={groupOptions}
            newGroupName={newGroupName}
            setAutomationName={setAutomationName}
            setExistingGroup={setExistingGroup}
            setGroupMode={setGroupMode}
            setNewGroupName={setNewGroupName}
            setTaskDescription={setDescription}
            taskDescription={automationForm.description.blob}
          />
        }

        {
          currentStepKey === 'RULES' &&
          <AutomationsStepRules
            automationForm={automationForm}
            initAssigneesInfo={automation?.initAssigneesInfo}
            isTriggerLocked={isTriggerLocked}
            setAssigneeRns={setAutomationAssigneeRns}
            setPlaybookRn={setAutomationPlaybookRn}
            setPrompt={setAutomationPrompt}
            setResourceType={setAutomationResourceType}
            setUpdateRuleField={setAutomationUpdateRuleField}
          />
        }

        <Box sx={{borderBottom: '1px solid #EEE8E2'}}></Box>

        <Stack
          direction='row'
          justifyContent='flex-end'
          sx={{
            px: {
              xs: '20px',
              sm: '26px',
            },
            py: '12px',
            gap: '10px',
          }}
        >
          {
            (
              currentStepKey === 'BASIC' ? [
                {
                  key: 'cancel',
                  label: translate('automations.Automations.New.Actions.Cancel'),
                  onClick: closeDialog,
                  disabled: false,
                  variant: 'outlined',
                },
                {
                  key: 'next',
                  label: translate('automations.Automations.New.Actions.Next'),
                  onClick: goToNextStep,
                  disabled: !isBasicStepValid,
                  variant: 'contained',
                },
              ] : [
                {
                  key: 'back',
                  label: translate('automations.Automations.New.Actions.Back'),
                  onClick: goToPreviousStep,
                  disabled: false,
                  variant: 'outlined',
                },
                {
                  key: 'create',
                  label: translate('automations.Automations.New.Actions.Create'),
                  onClick: handleSubmit,
                  disabled: isSubmitDisabled,
                  variant: 'contained',
                },
              ]
            ).map((actionConfig) => {
              return (
                <Button
                  key={actionConfig.key}
                  onClick={actionConfig.onClick}
                  variant={actionConfig.variant}
                  disableElevation
                  disabled={actionConfig.disabled}
                  sx={
                    actionConfig.variant === 'outlined' ? {
                      'borderRadius': '7px',
                      'px': '10px',
                      'py': '10px',
                      'fontSize': '12px',
                      'fontWeight': 400,
                      'lineHeight': '100%',
                      'textTransform': 'none',
                      'color': '#6B6760',
                      'borderColor': '#E8E4DE',
                      'backgroundColor': '#FFFFFF',
                      '&:hover': {
                        backgroundColor: '#FFFFFF',
                        borderColor: '#E8E4DE',
                        boxShadow: 'none',
                      },
                    } : {
                      'borderRadius': '7px',
                      'px': '10px',
                      'py': '10px',
                      'fontSize': '12px',
                      'fontWeight': 500,
                      'lineHeight': '100%',
                      'textTransform': 'none',
                      'color': '#FFFFFF',
                      'backgroundColor': '#DA7955',
                      '&:hover': {
                        backgroundColor: '#DA7955',
                        boxShadow: 'none',
                      },
                      '&.Mui-disabled': {
                        backgroundColor: '#E8B19D',
                        color: '#FFFFFF',
                      },
                    }
                  }
                >
                  {actionConfig.label}
                </Button>
              );
            })
          }
        </Stack>
      </Stack>
    </Dialog>
  );
};

AutomationsNew.propTypes = {
  groupOptions: PropTypes.arrayOf(PropTypes.string).isRequired,
  onClose: PropTypes.func.isRequired,
  handleCreateAutomation: PropTypes.func.isRequired,
  automation: PropTypes.shape({
    assigneeRns: PropTypes.array,
    groupName: PropTypes.string,
    name: PropTypes.string,
    description: PropTypes.shape({
      blob: PropTypes.string,
    }),
    outputType: PropTypes.string,
    playbookRn: PropTypes.string,
    prompt: PropTypes.string,
    resourceType: PropTypes.string,
    taskRuleRn: PropTypes.string,
    initAssigneesInfo: PropTypes.array,
    updateRules: PropTypes.array,
  }),
};

export default React.memo(AutomationsNew);
